import { useMemo } from 'react';
import type { LeagueBundle } from '../../shared/types';
import { Empty, Legend, PageHead, Section, TeamCell } from '../components/common';
import { efficiency, fmtPct, fmtPoints, fmtRecord } from '../lib/format';
import { buildTeamViews, isPreseason, sortStandings } from '../lib/league';

export default function Standings({ bundle }: { bundle: LeagueBundle }) {
  const teams = useMemo(() => buildTeamViews(bundle), [bundle]);
  const standings = useMemo(() => sortStandings(teams), [teams]);
  const preseason = isPreseason(bundle);

  if (preseason) {
    return (
      <>
        <PageHead title="Standings" meta={[`${bundle.league.season} season`, 'Not started']} />
        <div className="card">
          <Empty>
            Nothing to rank until week 1 is final. <a href="/history">League history</a> has last
            season's finish.
          </Empty>
        </div>
      </>
    );
  }

  const playoffLine = bundle.league.playoff_teams;

  return (
    <>
      <PageHead
        title="Standings"
        meta={[
          `${bundle.league.season} season`,
          `Through week ${bundle.state.display_week}`,
          `Top ${playoffLine} make the playoffs, from week ${bundle.league.playoff_week_start}`,
        ]}
      />

      <Section
        title="League Table"
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="rank">#</th>
                <th>Team</th>
                <th className="num">W-L</th>
                <th className="num">PCT</th>
                <th className="num">PF</th>
                <th className="num">PA</th>
                <th className="num">DIFF</th>
                <th className="num">MAX</th>
                <th className="num">EFF</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((t, i) => {
                const place = i + 1;
                const diff = t.record.pointsFor - t.record.pointsAgainst;
                const eff = efficiency(t.record);
                return (
                  <tr
                    key={t.roster_id}
                    style={
                      place === playoffLine
                        ? { borderBottom: '2px solid var(--ember)' }
                        : undefined
                    }
                  >
                    <td className={`rank ${place === 1 ? 'top' : ''}`}>{place}</td>
                    <td>
                      <TeamCell team={t} />
                    </td>
                    <td className="num">{fmtRecord(t.record)}</td>
                    <td className="num faint">
                      {fmtPct(
                        (t.record.wins + t.record.ties * 0.5) /
                          Math.max(1, t.record.wins + t.record.losses + t.record.ties),
                        0,
                      )}
                    </td>
                    <td className="num">{fmtPoints(t.record.pointsFor)}</td>
                    <td className="num faint">{fmtPoints(t.record.pointsAgainst)}</td>
                    <td className={`num ${diff >= 0 ? 'good' : 'bad'}`}>
                      {diff >= 0 ? '+' : ''}
                      {fmtPoints(diff)}
                    </td>
                    <td className="num faint">
                      {t.record.potentialPoints ? fmtPoints(t.record.potentialPoints) : '—'}
                    </td>
                    <td className="num">{eff == null ? '—' : fmtPct(eff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            { term: 'MAX', def: 'best possible score with an optimal lineup' },
            { term: 'EFF', def: 'points scored over MAX' },
            { term: 'Orange line', def: 'playoff cut' },
          ]}
        />
      </Section>
    </>
  );
}
